import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import client, { apiCall } from '../../api/client';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import CopyButton from '../../components/CopyButton';
import Loader from '../../components/Loader';

const EMPTY_FORM = { amount: '', txHash: '', proof: null };

export default function Wallet() {
  const [balance, setBalance] = useState(null);
  const [depositAddress, setDepositAddress] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [deposits, setDeposits] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    apiCall(client.get('/users/get_profile')).then((u) => setBalance(u.walletBalance || 0)).catch(() => setBalance(0));
    apiCall(client.get('/wallet/deposit_address')).then(setDepositAddress).catch((e) => setError(e.message));
    apiCall(client.get('/wallet/deposit/my'))
      .then(setDeposits)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  useEffect(() => {
    if (!depositAddress) {
      setQrDataUrl('');
      return;
    }
    QRCode.toDataURL(depositAddress, { width: 180, margin: 1 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [depositAddress]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!form.proof) {
      setError('Please attach a screenshot of the deposit as proof.');
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append('amount', form.amount);
      data.append('txHash', form.txHash);
      data.append('proof', form.proof);
      await apiCall(client.post('/wallet/deposit/submit', data));
      setSuccess('Deposit submitted. Our team will review it shortly.');
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { key: 'amount', label: 'Amount claimed', render: (row) => `$${row.amount}` },
    { key: 'txHash', label: 'Tx hash', render: (row) => <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }}>{row.txHash}</span> },
    { key: 'proofUrl', label: 'Proof', render: (row) => <a href={apiOrigin() + row.proofUrl} target="_blank" rel="noreferrer">View</a> },
    { key: 'status', label: 'Status', render: (row) => <StatusBadge value={row.status} /> },
    { key: 'creditedAmount', label: 'Credited', render: (row) => row.creditedAmount != null ? `$${row.creditedAmount}` : '—' },
    { key: 'adminNote', label: 'Note', render: (row) => row.adminNote || '—' },
  ];

  return (
    <div>
      <div className="card-header">
        <h1>Wallet</h1>
      </div>
      {error && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}

      <div className="card" style={{ marginBottom: 24 }}>
        <p className="profile-section-title">Balance</p>
        <p style={{ fontSize: 32, fontWeight: 800, margin: '4px 0 0' }}>
          {balance == null ? <Loader label="" /> : `$${balance}`}
        </p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header"><h3>Deposit funds (BEP20)</h3></div>
        <p className="profile-section-desc">
          Send USDT on the BNB Smart Chain (BEP20) to the address below, then submit the
          transaction hash and a screenshot as proof.
          <br />
          Your wallet will be credited automatically.
        </p>
        <div className="">
          {qrDataUrl && (
            <img
              src={qrDataUrl}
              alt="Deposit address QR code"
              width={140}
              height={140}
              style={{ borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', flexShrink: 0 }}
            />
          )}
          <div className="form-group" style={{ flex: 1, marginTop: 10 }}>
            <label>Deposit address</label>
            <div className="key-row key-mobile-row">
              <span style={{ wordBreak: 'break-all' }}>{depositAddress || 'Loading...'}</span>
              {depositAddress && <CopyButton text={depositAddress} />}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-row wallet-amount-row">
            <div className="form-group">
              <label>Amount sent ($)</label>
              <input
                type="number" min="0" step="0.01" required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
            <div className="form-group" style={{ flex: 2 }}>
              <label>Transaction hash</label>
              <input
                required
                value={form.txHash}
                onChange={(e) => setForm({ ...form, txHash: e.target.value })}
                placeholder="0x..."
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label>Proof screenshot</label>
            <input
              type="file" accept="image/jpeg,image/png,image/webp" required
              onChange={(e) => setForm({ ...form, proof: e.target.files[0] || null })}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit deposit'}
          </button>
        </form>
      </div>

      <div className="card">
        <div className="card-header"><h3>Deposit history</h3></div>
        {loading ? (
          <Loader label="Loading deposit history..." />
        ) : (
          <DataTable columns={columns} rows={deposits} emptyMessage="You haven't submitted any deposits yet." />
        )}
      </div>
    </div>
  );
}

function apiOrigin() {
  const base = client.defaults.baseURL || '';
  return base.replace(/\/api\/?$/, '');
}
